import { ExtraBoldText, StepAssetBreakdownPopoverChip } from "@stepfinance/react";
import type { LockedStakingPosition } from "@stepfinance/staking";
import type { FC } from "react";
import Countdown from "react-countdown";

import type { CommonStrings, SingleAssetStakingStrings } from "../../../contexts/language/languageTypes";
import type { StakingPositions } from "../../../types/staking";
import { isAtlasLockPosition, isLockedStakingPosition, isPolisLockPosition } from "../../../types/staking";
import { countdownRenderString, daysToYearsAndRemainingDayStr } from "../../../utils/utils";
import { APRBreakdownInfo } from "../../shared";
import CellValue from "../../Table/CellValue";

const tokenLockupPopoverContent = (
  record: LockedStakingPosition,
  common: CommonStrings,
  singleAssetStakingText: SingleAssetStakingStrings,
) => {
  let multiplier: number | undefined;
  let marketplaceFee: number | undefined;
  let withdrawInitiated: boolean | undefined;
  if (isPolisLockPosition(record)) {
    multiplier = record.multiplier;
  }
  if (isAtlasLockPosition(record)) {
    marketplaceFee = record.marketplaceFee;
    withdrawInitiated = record.withdrawInitiated;
  }
  const { amountStaked, stakeEndTs, stakeDuration } = record;
  const { yearStr, totalDurationStr } = daysToYearsAndRemainingDayStr(stakeDuration, common);
  return (
    <>
      <APRBreakdownInfo width={yearStr ? 248 : 212}>
        <div>
          <ExtraBoldText>{singleAssetStakingText.lockedFor}</ExtraBoldText>
          <CellValue cellType="number">{totalDurationStr}</CellValue>
        </div>
        {multiplier !== undefined && (
          <>
            <div>
              <ExtraBoldText>{singleAssetStakingText.multiplier}</ExtraBoldText>
              <CellValue cellType="number">{multiplier.toFixed(2) + "x"}</CellValue>
            </div>
            <div>
              <ExtraBoldText>{singleAssetStakingText.votingPower}</ExtraBoldText>
              <CellValue cellType="number">{(multiplier * amountStaked).toFixed(2) + " PVP"}</CellValue>
            </div>
          </>
        )}
        {withdrawInitiated !== undefined && (
          <>
            <div>
              <ExtraBoldText>{singleAssetStakingText.marketplaceFee}</ExtraBoldText>
              <CellValue cellType="percentage">{marketplaceFee}</CellValue>
            </div>
            <div>
              <ExtraBoldText>{singleAssetStakingText.withdrawInitiated}</ExtraBoldText>
              <CellValue cellType="text">{withdrawInitiated ? common.yes : common.no}</CellValue>
            </div>
          </>
        )}
        {withdrawInitiated !== false && (
          <div>
            <ExtraBoldText>{singleAssetStakingText.timeRemaining}</ExtraBoldText>
            <Countdown
              date={stakeEndTs * 1000}
              renderer={(args) => <CellValue cellType="number">{countdownRenderString(args)}</CellValue>}
            ></Countdown>
          </div>
        )}
      </APRBreakdownInfo>
    </>
  );
};

interface TokenLockupPopoverProps {
  record: StakingPositions;
  common: CommonStrings;
  singleAssetStakingText: SingleAssetStakingStrings;
}

export const TokenLockupPopover: FC<TokenLockupPopoverProps> = ({ record, common, singleAssetStakingText }) => {
  return isLockedStakingPosition(record) ? (
    <StepAssetBreakdownPopoverChip
      onlyContent={true}
      title={singleAssetStakingText.lockingPeriod}
      content={tokenLockupPopoverContent(record, common, singleAssetStakingText)}
    />
  ) : (
    <></>
  );
};
